import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Youtube, Upload, MonitorPlay, StopCircle, Radio, ShieldCheck, Eye, Rss, BellRing, X } from "lucide-react";
import { api, apiError, wsUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LiveVideoPlayer } from "../components/webinar/LiveVideoPlayer";

export default function WebinaireAnimer() {
  const { hostToken } = useParams();
  const [info, setInfo] = useState(null);
  const [video, setVideo] = useState({ source: null });
  const [err, setErr] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewers, setViewers] = useState(0);
  const [liveActive, setLiveActive] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [showAlert, setShowAlert] = useState(false);
  const alertedRef = useRef(false);
  const lastCtrl = useRef(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/webinars/host/${hostToken}`); setInfo(data.webinar); setVideo(data.video || { source: null }); setViewers(data.viewers || 0); setLiveActive(!!data.webinar?.live_active); }
    catch (e) { setErr(apiError(e, "Lien animateur invalide.")); }
  }, [hostToken]);
  useEffect(() => { load(); }, [load]);

  // WS animateur : compteur d'audience en temps réel (role=host, non compté)
  const wid = info?.id;
  useEffect(() => {
    if (!wid) return;
    let ws;
    try {
      ws = new WebSocket(wsUrl(`/ws/webinars/${wid}?role=host`));
      ws.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m.type === "viewers") setViewers(m.count); } catch {} };
      const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 25000);
      ws.onclose = () => clearInterval(ping);
    } catch {}
    return () => { try { ws && ws.close(); } catch {} };
  }, [wid]);

  const toggleLive = async () => {
    const next = !liveActive;
    try { await api.post(`/webinars/host/${hostToken}/live-active`, { active: next }); setLiveActive(next); toast.success(next ? "Marqué EN DIRECT (badge visible publiquement)" : "Direct arrêté"); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Alerte pic d'audience : quand l'audience franchit le seuil
  useEffect(() => {
    const th = Number(threshold) || 0;
    if (th > 0 && viewers >= th && !alertedRef.current) {
      alertedRef.current = true; setShowAlert(true);
      toast.success(`🎉 ${viewers} participants en ligne — c'est le moment de lancer un sondage ou une Q&A !`);
    } else if (viewers < th) {
      alertedRef.current = false; setShowAlert(false);
    }
  }, [viewers, threshold]);

  const shareYoutube = async () => {
    if (!ytUrl.trim()) { toast.error("Collez un lien YouTube"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/webinars/host/${hostToken}/video`, { source: "youtube", url: ytUrl.trim() }); setVideo(data.video); toast.success("Vidéo YouTube partagée"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const shareFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/webinars/host/${hostToken}/video/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post(`/webinars/host/${hostToken}/video`, { source: "file", url: data.url });
      setVideo(res.data.video); toast.success("Vidéo partagée");
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); } finally { setUploading(false); }
  };

  const stopShare = async () => {
    try { await api.post(`/webinars/host/${hostToken}/video`, { source: "none" }); setVideo({ source: null }); toast.success("Partage arrêté"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const control = useCallback(async (action, position) => {
    const now = Date.now();
    if (action === "play" && now - lastCtrl.current < 1500) { /* throttle re-cale */ }
    lastCtrl.current = now;
    try { await api.post(`/webinars/host/${hostToken}/video/control`, { action, position: position || 0 }); }
    catch (e) { /* silencieux pour ne pas spammer */ }
  }, [hostToken]);

  if (err) return <div className="mx-auto max-w-lg px-4 py-16 text-center"><p className="text-slate-500">{err}</p></div>;
  if (!info) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10" data-testid="webinaire-animer">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-semibold text-fuchsia-700"><ShieldCheck size={13} /> Interface animateur</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy"><Radio size={13} /> {info.title}</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="animer-viewers"><Eye size={13} className="text-fuchsia-600" /> {viewers} en ligne</span>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600" title="Seuil d'alerte d'audience">
          <BellRing size={13} className="text-amber-500" /> Alerte à
          <input type="number" min="1" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="animer-threshold" className="w-12 rounded border border-slate-300 bg-white px-1 py-0.5 text-center text-xs" />
        </div>
        <Button size="sm" onClick={toggleLive} data-testid="animer-live-toggle"
          className={`ml-auto rounded-full text-xs ${liveActive ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>
          <Rss size={13} className="mr-1" /> {liveActive ? "● EN DIRECT — arrêter" : "Passer en direct"}
        </Button>
      </div>

      {showAlert && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-800" data-testid="animer-audience-alert">
          <BellRing size={18} className="shrink-0 text-amber-500" />
          <p className="flex-1 text-sm font-medium">Pic d'audience : <b>{viewers}</b> participants en ligne. Lancez un <b>sondage</b> ou une <b>session de questions</b> pour maximiser l'engagement !</p>
          <button onClick={() => setShowAlert(false)} data-testid="animer-audience-alert-close" className="text-amber-500 hover:text-amber-700"><X size={16} /></button>
        </div>
      )}
      <h1 className="font-display text-2xl font-bold text-navy">Partage vidéo en direct</h1>
      <p className="mt-1 text-sm text-slate-500">Partagez une vidéo YouTube ou un fichier de votre ordinateur. Lecture, pause et navigation sont synchronisées pour tous les participants — vous seul pilotez.</p>

      {/* Contrôles de source */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-navy"><Youtube size={16} className="text-red-600" /> Lien YouTube</h3>
          <Input value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…" data-testid="animer-yt-url" />
          <Button onClick={shareYoutube} disabled={busy} data-testid="animer-yt-share" className="mt-3 w-full rounded-full bg-navy text-sm text-white">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Partager cette vidéo"}
          </Button>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-navy"><Upload size={16} className="text-ciel" /> Vidéo depuis mon ordinateur</h3>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500 hover:border-ciel hover:text-ciel">
            {uploading ? <><Loader2 size={15} className="animate-spin" /> Téléversement…</> : <><MonitorPlay size={16} /> Choisir un fichier MP4 (max 300 Mo)</>}
            <input type="file" accept="video/*" className="hidden" data-testid="animer-file-input" disabled={uploading} onChange={(e) => shareFile(e.target.files?.[0])} />
          </label>
        </div>
      </div>

      {/* Lecteur + état */}
      {video.source ? (
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-navy">Vidéo en cours de partage — vos contrôles pilotent tout le monde</p>
            <Button variant="outline" onClick={stopShare} data-testid="animer-stop" className="rounded-full text-xs text-red-600"><StopCircle size={14} className="mr-1" /> Arrêter le partage</Button>
          </div>
          <LiveVideoPlayer state={video} mode="host" onControl={control} />
        </div>
      ) : (
        <div className="mt-6 rounded-2xl bg-slate-50 p-8 text-center text-sm text-slate-400" data-testid="animer-no-video">
          Aucune vidéo partagée pour le moment. Choisissez une source ci-dessus pour démarrer.
        </div>
      )}
    </div>
  );
}
